import type { ActionGraph, ActionType, EventType } from "../actions.ts";
import { getOutputHandles } from "../schemaSpec.ts";
import { issue } from "./helpers.ts";
import type { Issue, ValidationReport } from "./types.ts";

export const validateGraphEdges = (
	graph: ActionGraph,
	entityId: string,
	report: ValidationReport,
	entityKind: Issue["entityKind"] = "items",
): void => {
	graph.edges.forEach((edge, idx) => {
		const sourceNode = graph.nodes[edge.source];
		if (!sourceNode) {
			report.errors.push(
				issue("error", entityKind, entityId, ["graph", "edges", idx, "source"],
					`Edge source '${edge.source}' does not exist`, "orphan_edge",
					{ nodeId: edge.source }),
			);
			return;
		}

		const handles = getOutputHandles(sourceNode.type as ActionType | EventType);

		const dynamicHandles =
			sourceNode.type === "Choice"
				? (((sourceNode as unknown as Record<string, unknown>).cases as
						| Array<{ handle?: string }>
						| undefined) ?? []).map((c) => c.handle).filter((h): h is string => typeof h === "string")
				: [];
		const allHandles = handles ? [...handles, ...dynamicHandles] : dynamicHandles;

		if (handles !== undefined && !allHandles.includes(edge.sourceHandle)) {
			report.warnings.push(
				issue("warning", entityKind, entityId, ["graph", "edges", idx, "sourceHandle"],
					`Orphan edge: sourceHandle '${edge.sourceHandle}' on ${sourceNode.type} node no longer matches any output — this branch will be ignored at runtime`,
					"invalid_handle",
					{ nodeId: sourceNode.id, actionType: sourceNode.type, fieldName: "sourceHandle" }),
			);
		}
		if (!graph.nodes[edge.target]) {
			report.errors.push(
				issue("error", entityKind, entityId, ["graph", "edges", idx, "target"],
					`Edge target '${edge.target}' does not exist`, "orphan_edge",
					{ nodeId: edge.target }),
			);
		}
	});
};
